'use client';

// Cache khởi động (localStorage) — cắt "màn hình chờ trắng" lúc mở app trên mạng yếu.
//
// VẤN ĐỀ: trước đây mỗi lần mở app phải chờ TUẦN TỰ xong 3 chặng mạng mới hiện được pixel đầu tiên:
//   onAuthStateChanged → POST /api/auth/sync-profile → getDoc(tenants/{tid}) → getDocs(buildings)
// Trên 4G yếu (NV/chủ nhà đi đường) chuỗi này kéo dài hàng chục giây, user chỉ thấy ô nhấp nháy.
//
// CÁCH LÀM: lần đăng nhập trước lưu lại hồ sơ + tenant + danh sách toà. Lần sau hiện UI NGAY từ cache,
// đồng thời vẫn gọi sync-profile ở nền và ghi đè khi có kết quả thật (self-healing).
//
// ⚠️ KHÔNG PHẢI RANH GIỚI BẢO MẬT. Cache chỉ quyết định UI hiện gì trong ~vài trăm ms đầu.
// Quyền thật do Firestore Security Rules (custom claim tenantId/gmsRole) quyết định — sửa tay
// localStorage KHÔNG đọc/ghi thêm được gì. Hồ sơ bị thu quyền → sync-profile trả về profile mới
// (hoặc null → đăng xuất) và cache bị ghi đè/xoá ngay sau đó.

import type { GMSUser } from '@/types';
import { readPrefJson, removePref, writePrefJson } from '@/lib/prefs';
import { reviveTimestamps } from '@/lib/swr-cache';

const KEY = 'gms-boot-cache';
const MAX_AGE_MS = 7 * 86_400_000; // quá 7 ngày coi như cũ → chờ server, không hiện cache

/** Ảnh chụp tenant do /api/auth/sync-profile trả kèm (thay cho getDoc(tenants/{tid}) ở client). */
export interface BootTenant {
  id: string;
  name: string;
  plan: string | null;
  branding: { logoUrl?: string; brandColor?: string } | null;
  limits: Record<string, number> | null;
  billingStatus: string | null;
  billingLockAt: number | null;
  suspended: boolean;
  /** Optional: cache cũ (trước khi có HOLA) không có field này → coi là false. */
  holaEnabled?: boolean;
}

interface BootCache {
  uid: string;
  savedAt: number;
  profile: GMSUser;
  tenant: BootTenant | null;
  /** Danh sách toà của tenant (BuildingProvider) — hiếm khi đổi, hiện ngay rồi làm mới ở nền. */
  buildings?: unknown[];
}

function read(): BootCache | null {
  if (typeof window === 'undefined') return null;
  const raw = readPrefJson<BootCache | null>(KEY, null);
  if (!raw) return null;
  // Dựng lại Timestamp thật (xem lib/swr-cache.ts reviveTimestamps) — `buildings` có field
  // `createdAt`/`history` kiểu Timestamp, mất PROTOTYPE khi qua JSON localStorage y hệt cơ chế
  // đã crash `/timeline` 2 lần (20/09 + 23/09). Chưa có chỗ nào đọc 2 field đó từ cache boot NÀY nên
  // chưa tự lộ ra, nhưng đóng gói sẵn ở đây rẻ hơn nhiều so với chờ crash lần 3 rồi mới vá.
  const c = reviveTimestamps(raw) as BootCache;
  if (!c?.uid || !c.profile || Date.now() - (c.savedAt ?? 0) > MAX_AGE_MS) return null;
  return c;
}

function write(patch: Partial<BootCache> & { uid: string }): void {
  if (typeof window === 'undefined') return;
  try {
    const cur = read();
    const next: BootCache = {
      ...(cur?.uid === patch.uid ? cur : { uid: patch.uid, profile: patch.profile as GMSUser, tenant: null }),
      ...patch,
      savedAt: Date.now(),
    };
    if (!next.profile) return;
    writePrefJson(KEY, next);
  } catch { /* quota đầy / storage bị chặn → bỏ qua, chỉ mất tối ưu */ }
}

// ⚠️ Bug thật 2026-09-14, ĐÃ FIX (commit aba7dafe) — cache chỉ khoá theo `uid`, KHÔNG khoá theo
// tenant đang hoạt động. User `workspaceSwitch`/impersonate (1 tài khoản làm việc nhiều tenant, vd
// gms.support@ledifa.com) đổi qua lại giữa các workspace → cache có thể còn giữ tenant CŨ từ lần
// trước → UI hiện NGAY tenant SAI trong ~1-3s đầu mỗi lần tải trang → các hook fetch một lượt
// (dashboard/todos/unread-chats/analytics, cả đọc qua TenantProvider lẫn đọc thẳng useAuth().user)
// fire nhầm tenant → PERMISSION_DENIED hàng loạt (33 dòng /reports 2026-09-14 09:18-10:25).
// Fix: `lib/auth.tsx` + `lib/tenant-context.tsx` đối chiếu tenantId cache với claim token HIỆN TẠI
// (`getIdTokenResult(false)` — đọc local, không round-trip mạng, gần như tức thì) TRƯỚC khi tin;
// lệch thì bỏ qua cache, rơi xuống đường chậm tự đọc lại đúng — giữ được tốc độ cache cho trường hợp
// phổ biến (không đổi workspace) mà vẫn đúng cho trường hợp multi-workspace.
/** Hồ sơ + tenant đã cache cho ĐÚNG uid đang đăng nhập (null nếu khác user / hết hạn / không có). */
export function readBootCache(uid: string): BootCache | null {
  const c = read();
  return c && c.uid === uid ? c : null;
}

export function saveBootProfile(uid: string, profile: GMSUser, tenant: BootTenant | null): void {
  write({ uid, profile, tenant });
}

export function saveBootBuildings(uid: string, buildings: unknown[]): void {
  if (!read()?.profile) return; // chưa có hồ sơ trong cache → không tạo bản ghi cụt
  write({ uid, buildings });
}

export function clearBootCache(): void {
  if (typeof window === 'undefined') return;
  removePref(KEY);
}
